import axios, { AxiosResponse } from 'axios'

import gql from '@js/api/utils/gql'
import { env } from '@js/utils/env'
import getStoreName from '@js/api/utils/getStoreName'
import getBearerToken from '@js/api/utils/getBearerToken'
import { CustomerProfile } from '@js/api/types/customer/CustomerProfile'

interface UpdateCustomerProfilePayload {
  customerId: number
  email: string
  secondaryEmail: string
  firstName: string
  middleName: string | null
  lastName: string
  customerContactNumber: string
  personal: {
    address: string
    addressTwo: string
    city: string
    country: string
    phone: string
    phoneExt: string
    phoneType: string
    postcode: string
    secondaryPhone: string
    state: string
  }
  professional: {
    managementLevel: string
    primaryEmployerName: string
    primaryEmployerCity: string
    primaryEmployerState: string
    primaryPlaceOfEmployment: string
    wcCertificates: string[]
  }
}

interface UpdateCustomerProfileResponse {
  data: {
    wceiUpdateCustomerProfile: CustomerProfile | null
  }
  errors?: [{ message: string }]
}

export const updateCustomerProfileMutation = gql`
  mutation(
    $email: String!,
    $secondaryEmail: String,
    $firstName: String!,
    $middleName: String,
    $lastName: String!,
    $customerContactNumber: String!,
    $address: String!,
    $addressTwo: String,
    $city: String!,
    $state: String!,
    $postcode: String!,
    $country: String!,
    $phone: String!,
    $phoneExt: String,
    $phoneType: String,
    $secondaryPhone: String,
    $managementLevel: String!,
    $primaryEmployerName: String!,
    $primaryEmployerCity: String!,
    $primaryEmployerState: String!,
    $primaryPlaceOfEmployment: String!,
    $wcCertificates: [String!]
  ) {
    wceiUpdateCustomerProfile(
      input: {
        email: $email,
        secondary_email: $secondaryEmail,
        first_name: $firstName,
        middle_name: $middleName,
        last_name: $lastName,
        customer_contact_number: $customerContactNumber
        personal: {
          address: $address,
          address_two: $addressTwo,
          city: $city,
          state: $state,
          postcode: $postcode,
          country: $country,
          phone: $phone,
          phone_ext: $phoneExt,
          phone_type: $phoneType,
          secondary_phone: $secondaryPhone
        },
        professional: {
          management_level: $managementLevel,
          employer_name: $primaryEmployerName,
          employer_city: $primaryEmployerCity,
          employer_state: $primaryEmployerState,
          primary_employment: $primaryPlaceOfEmployment,
          wc_certificates: $wcCertificates
        }
      }
    ) {
      customer_id
      email
      secondary_email
      first_name
      last_name
      middle_name
      customer_contact_number
      personal {
        address
        address_two
        city
        country
        phone
        phone_ext
        phone_type
        postcode
        secondary_phone
        state
      }
      professional {
        hear_about
        management_level
        employer_city
        employer_name
        employer_state
        primary_employment
        wc_certificates
      }
    }
  }
`

/**
 * @throws {string}
 *
 * Will throw one error message; Magento only returns one at a time, rather than an array of all errors.
 */
export const updateCustomerProfile = async (payload: UpdateCustomerProfilePayload): Promise<CustomerProfile | null> => {
  const bearerToken = getBearerToken()

  const { data }: AxiosResponse<UpdateCustomerProfileResponse> = await axios({
    method: 'POST',
    url: env.magento.GRAPHQL_URL,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${bearerToken}`,
      Store: getStoreName(),
    },
    data: {
      query: updateCustomerProfileMutation,
      variables: {
        customerId: payload.customerId,
        email: payload.email,
        secondaryEmail: payload.secondaryEmail,
        firstName: payload.firstName,
        middleName: payload.middleName,
        lastName: payload.lastName,
        customerContactNumber: payload.personal.phone,
        address: payload.personal.address,
        addressTwo: payload.personal.addressTwo,
        city: payload.personal.city,
        state: payload.personal.state,
        postcode: payload.personal.postcode,
        country: payload.personal.country,
        phone: payload.personal.phone,
        phoneExt: payload.personal.phoneExt,
        phoneType: payload.personal.phoneType,
        secondaryPhone: payload.personal.secondaryPhone,
        managementLevel: payload.professional.managementLevel,
        primaryEmployerName: payload.professional.primaryEmployerName,
        primaryEmployerCity: payload.professional.primaryEmployerCity,
        primaryEmployerState: payload.professional.primaryEmployerState,
        primaryPlaceOfEmployment: payload.professional.primaryPlaceOfEmployment,
        wcCertificates: payload.professional.wcCertificates,
      },
    },
  })

  if (data.errors) {
    throw data.errors[0].message
  }

  if (data.data.wceiUpdateCustomerProfile) {
    return data.data.wceiUpdateCustomerProfile
  }

  return null
}
